import { useCallback, useEffect, useState } from 'react';

import { fetchJson } from '@api/ApiHelper';

export interface CustomPermission {
  key?: string;
  label: string;
  asset_approve: boolean;
  add_to_shares: boolean;
  view_asset_usage: boolean;
  edit_brandguide: boolean;
}

export interface CustomRoleWithUsage extends CustomPermission {
  key: string;
}

// In-memory cache to avoid duplicate requests and share state across components
// Keyed by organization key
const CUSTOM_ROLE_CACHE = new Map<string, CustomRoleWithUsage | null>();
const CUSTOM_ROLE_INFLIGHT = new Map<string, Promise<CustomRoleWithUsage | null>>();
const CUSTOM_ROLE_SUBSCRIBERS =
  new Map<string, Set<(role: CustomRoleWithUsage | null, error: string | null) => void>>();
const CUSTOM_ROLE_CACHE_TS = new Map<string, number>();
const CUSTOM_ROLE_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

const setCustomRoleCache = (orgKey: string, role: CustomRoleWithUsage | null): void => {
  CUSTOM_ROLE_CACHE.set(orgKey, role);
  CUSTOM_ROLE_CACHE_TS.set(orgKey, Date.now());
  const subs = CUSTOM_ROLE_SUBSCRIBERS.get(orgKey);
  if (subs) {
    subs.forEach((cb) => {
      try { cb(role, null); } catch { /* noop */ }
    });
  }
};

const broadcastError = (orgKey: string, error: string | null): void => {
  const subs = CUSTOM_ROLE_SUBSCRIBERS.get(orgKey);
  if (subs) {
    subs.forEach((cb) => {
      try { cb(CUSTOM_ROLE_CACHE.get(orgKey) ?? null, error); } catch { /* noop */ }
    });
  }
};

const fetchAndCacheCustomRole = async (
  orgKey: string,
): Promise<CustomRoleWithUsage | null> => {
  const existing = CUSTOM_ROLE_INFLIGHT.get(orgKey);
  if (existing !== undefined) {
    return existing;
  }

  const promise: Promise<CustomRoleWithUsage | null> = (async (): Promise<CustomRoleWithUsage | null> => {
    // Fetch directly to avoid forward reference to getCustomPermissions
    const response = await fetchJson<CustomPermissionsListResponse>({
      url: `/api/v4/organizations/${orgKey}/custom_permissions`,
      method: 'GET',
    });
    const roleData = response.data?.[0];
    const roleWithUsage: CustomRoleWithUsage | null = roleData
      ? { ...roleData.attributes, key: roleData.id }
      : null;
    setCustomRoleCache(orgKey, roleWithUsage);
    return roleWithUsage;
  })()
    .catch((err) => {
      console.error('Error fetching custom role:', err);
      broadcastError(orgKey, 'Failed to load custom role');
      throw err;
    })
    .finally(() => {
      CUSTOM_ROLE_INFLIGHT.delete(orgKey);
    });

  CUSTOM_ROLE_INFLIGHT.set(orgKey, promise);
  return promise;
};

export interface CustomPermissionResponse {
  data: {
    id: string;
    type: 'custom_permissions';
    attributes: CustomPermission;
  };
}

export interface CustomPermissionsListResponse {
  data: Array<{
    id: string;
    type: 'custom_permissions';
    attributes: CustomPermission;
  }>;
}

// Create a new custom permission
export const createCustomPermission = async (
  organizationKey: string,
  permission: Omit<CustomPermission, 'key'>
): Promise<CustomPermissionResponse> => {
  const response = await fetchJson<CustomPermissionResponse>({
    url: `/api/v4/organizations/${organizationKey}/custom_permissions`,
    method: 'POST',
    body: {
      data: {
        attributes: permission,
      },
    },
  });
  const roleWithUsage: CustomRoleWithUsage = {
    ...response.data.attributes,
    key: response.data.id,
  };
  setCustomRoleCache(organizationKey, roleWithUsage);
  return response;
};

// Update an existing custom permission
export const updateCustomPermission = async (
  organizationKey: string,
  key: string,
  permission: Omit<CustomPermission, 'key'>
): Promise<CustomPermissionResponse> => {
  const response = await fetchJson<CustomPermissionResponse>({
    url: `/api/v4/organizations/${organizationKey}/custom_permissions/${key}`,
    method: 'PUT',
    body: {
      data: {
        attributes: permission,
      },
    },
  });
  const roleWithUsage: CustomRoleWithUsage = {
    ...response.data.attributes,
    key: response.data.id,
  };
  setCustomRoleCache(organizationKey, roleWithUsage);
  return response;
};

// Delete a custom permission
export const deleteCustomPermission = async (
  organizationKey: string,
  key: string
): Promise<void> => {
  await fetchJson<void>({
    url: `/api/v4/organizations/${organizationKey}/custom_permissions/${key}`,
    method: 'DELETE',
  });
  // Since there can only be one custom role per org, null the cache
  setCustomRoleCache(organizationKey, null);
};

// Get all custom permissions for an organization
export const getCustomPermissions = async (
  organizationKey: string
): Promise<CustomPermissionsListResponse> => {
  return fetchJson<CustomPermissionsListResponse>({
    url: `/api/v4/organizations/${organizationKey}/custom_permissions`,
    method: 'GET',
  });
};

interface UseCustomRoleReturn {
  customRole: CustomRoleWithUsage | null;
  loading: boolean;
  error: string | null;
  refreshCustomRole: () => Promise<void>;
}

/**
 * Hook to fetch and manage custom role data for an organization
 *
 * @param organizationKey - The organization key to fetch custom roles for
 * @returns Object containing the custom role data, loading state, error state, and refresh function
 */
export const useCustomRole = (organizationKey?: string): UseCustomRoleReturn => {
  // Get organization key from global BFG object if not provided
  const orgKey =
    organizationKey ||
    (window as unknown as {
      BFG?: { organization_key?: string; hasFeature?: (key: string) => boolean };
    }).BFG?.organization_key;

  const featureOn =
    ((window as unknown as { BFG?: { hasFeature?: (key: string) => boolean } })
      .BFG?.hasFeature?.('custom_permissions_rollout')) ?? false;

  const initialRole = orgKey ? (CUSTOM_ROLE_CACHE.get(orgKey) ?? null) : null;
  const [customRole, setCustomRole] = useState<CustomRoleWithUsage | null>(initialRole);
  const [loading, setLoading] = useState<boolean>(!initialRole && !!featureOn);
  const [error, setError] = useState<string | null>(null);

  const fetchCustomRole = useCallback(async (): Promise<void> => {
    if (!orgKey) {
      setError('Organization key is required');
      setLoading(false);
      return;
    }
    if (!featureOn) {
      // Feature disabled: no fetch, clear value
      setCustomRole(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      await fetchAndCacheCustomRole(orgKey);
      setError(null);
    } catch (err) {
      setError('Failed to load custom role');
    } finally {
      setLoading(false);
    }
  }, [orgKey, featureOn]);

  useEffect(() => {
    if (!orgKey) {
      setError('Organization key is required');
      setLoading(false);
      return;
    }

    if (!featureOn) {
      // Feature disabled: skip fetching
      setCustomRole(null);
      setLoading(false);
      return;
    }

    // Subscribe to shared cache updates
    const cb = (role: CustomRoleWithUsage | null, err: string | null): void => {
      setCustomRole(role);
      setError(err);
    };
    let setForOrg = CUSTOM_ROLE_SUBSCRIBERS.get(orgKey);
    if (!setForOrg) {
      setForOrg = new Set();
      CUSTOM_ROLE_SUBSCRIBERS.set(orgKey, setForOrg);
    }
    setForOrg.add(cb);

    // If there is no cache or it is stale, fetch; otherwise, mark loading false
    const ts = CUSTOM_ROLE_CACHE_TS.get(orgKey) ?? 0;
    const isStale = Date.now() - ts > CUSTOM_ROLE_CACHE_TTL_MS;
    if (!CUSTOM_ROLE_CACHE.has(orgKey) || isStale) {
      setLoading(true);
      fetchAndCacheCustomRole(orgKey)
        .then(() => setError(null))
        .catch(() => setError('Failed to load custom role'))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }

    return () => {
      const subs = CUSTOM_ROLE_SUBSCRIBERS.get(orgKey);
      subs?.delete(cb);
    };
  }, [orgKey, featureOn]);

  return {
    customRole,
    loading,
    error,
    refreshCustomRole: fetchCustomRole,
  };
};
